// Copyright 2026, Auxilio Technologies LLC

// Full Tailwind class strings per order status (so the content scan keeps them).
export const STATUS_BADGE: Record<string, string> = {
  new: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  in_prep: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  ready: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  out_for_delivery: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

export const SLOT_ORDER = ['before_11', 'slot_12', 'slot_1', 'slot_3', 'after_4'];
export const ORDER_STATUSES = [
  'new',
  'in_prep',
  'ready',
  'out_for_delivery',
  'delivered',
  'failed',
  'cancelled',
];
export const PAYMENT_STATUSES = ['paid', 'unpaid', 'refunded'];
export const NOTIFY_MODES = ['auto', 'manual', 'silent'];

export function formatMoney(cents?: number | null): string {
  if (cents === null || cents === undefined) return '—';
  return `£${(cents / 100).toFixed(2)}`;
}

export function getLocalDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function sortBySlot<T extends { delivery_time_slot?: string; delivery_date?: string }>(orders: T[]): T[] {
  return [...orders].sort((a, b) => {
    const dateCmp = (a.delivery_date || '').localeCompare(b.delivery_date || '');
    if (dateCmp !== 0) return dateCmp;
    return SLOT_ORDER.indexOf(a.delivery_time_slot || '') - SLOT_ORDER.indexOf(b.delivery_time_slot || '');
  });
}

// Deep link to the device's native maps app for a single stop.
export function mapsLink(address: string): string {
  const q = encodeURIComponent(address);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return isIOS ? `https://maps.apple.com/?q=${q}` : `https://www.google.com/maps/search/?api=1&query=${q}`;
}

// Starting point for the driver's multi-stop route. For the demo this is fixed to
// London so the route makes sense regardless of where the browser actually is
// (testers are often outside the UK). In production this would be the shop's address.
export const ROUTE_ORIGIN = 'London, United Kingdom';

// Build a single Google Maps directions trip through every stop, in order.
// The last address is the destination and the rest are waypoints (driving mode).
// `origin` sets the start (default: London); pass '' to use the device's current location.
// Google's consumer URL keeps the given order (the manager's planned sequence);
// true geographic optimization would require a routing engine (future OSRM work).
export function routeLink(addresses: string[], origin: string = ROUTE_ORIGIN): string {
  const addrs = addresses.filter(Boolean);
  if (addrs.length === 0) return '';
  const destination = encodeURIComponent(addrs[addrs.length - 1]);
  // Google's free consumer URL supports up to ~9 intermediate waypoints.
  const waypoints = addrs.slice(0, -1).slice(0, 9).map(encodeURIComponent).join('%7C');
  let url = 'https://www.google.com/maps/dir/?api=1';
  if (origin) url += `&origin=${encodeURIComponent(origin)}`;
  url += `&destination=${destination}&travelmode=driving`;
  if (waypoints) url += `&waypoints=${waypoints}`;
  return url;
}
